import React, { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { DragDropContext, Droppable, DropResult, Draggable } from '@hello-pangea/dnd'
import { createCn } from '../../../helpers/utils'
import classes from '../styles.module.scss'
import { Chevron, Plus } from '../../../assets'
import { Meeting } from '../files.page'
import FileRow from './row.component'
import { useTranslation } from 'react-i18next'
import { MeetingProgress } from '../../../services/api.service'

interface IProps {
  setList: React.Dispatch<React.SetStateAction<Meeting[]>>;
  currentItems: Meeting[];
  filteredList: Meeting[];
  pageSize: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  type?: 'files' | 'favorites' | 'trash';
  viewMode?: 'list' | 'grid';
  onDragEnd?: (result: DropResult) => void;
  /** Задача L1: живой прогресс по id обрабатываемых встреч (см. useMeetingsProgress). */
  progressById?: Record<string, MeetingProgress>;
}

const cn = createCn('files', classes)

const FileList: FC<IProps> = ({
  setList,
  currentItems,
  filteredList,
  pageSize,
  page,
  setPage,
  totalPages,
  type = 'files',
  viewMode = 'list',
  onDragEnd,
  progressById,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canDrag = type !== 'trash' && viewMode === 'list' && Boolean(onDragEnd);

  const addButton = type === 'files' && (
    <button
      type="button"
      className={cn('add-placeholder')}
      onClick={() => navigate('/start-recording')}
    >
      <Plus />
      <span>{t('files.upload-new')}</span>
    </button>
  );

  const rows = currentItems.map((item, index) => {
    if (!canDrag) {
      return (
        <FileRow
          key={item.id}
          item={item}
          setList={setList}
          type={type}
          viewMode={viewMode}
          index={index}
          progress={progressById?.[item.id]}
        />
      );
    }

    return (
      <Draggable key={item.id} draggableId={item.id} index={index}>
        {(provided, snapshot) => (
          <FileRow
            item={item}
            setList={setList}
            type={type}
            viewMode={viewMode}
            index={index}
            showDragHandle
            innerRef={provided.innerRef}
            draggableProps={provided.draggableProps}
            dragHandleProps={provided.dragHandleProps}
            isDragging={snapshot.isDragging}
            progress={progressById?.[item.id]}
          />
        )}
      </Draggable>
    );
  });

  return (
    <>
      {canDrag ? (
        <DragDropContext onDragEnd={onDragEnd!}>
          <Droppable droppableId="meetings-list">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={cn('cards', { sortable: true })}
              >
                {rows}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          {addButton}
        </DragDropContext>
      ) : (
        <>
          <div className={cn('cards', { grid: viewMode === 'grid' })}>
            {rows}
          </div>
          {addButton}
        </>
      )}

      {currentItems.length === 0 && !filteredList.length && (
        <p className={cn('empty-state')}>{t('files.empty')}</p>
      )}

      {filteredList.length > pageSize && (
        <div className={cn('pagination')}>
          <Chevron
            onClick={() => page !== 1 && setPage(p => Math.max(1, p - 1))}
            className={cn('chevron', { disabled: page === 1 })}
          />

          <input
            type="number"
            min={1}
            max={totalPages}
            value={page}
            onChange={(e) => {
              const val = Number(e.target.value)
              if (!isNaN(val)) {
                setPage(Math.min(Math.max(1, val), totalPages))
              }
            }}
          />
          <span>{t('files.out-of')}</span>
          <span>{totalPages}</span>

          <Chevron
            onClick={() => page !== totalPages && setPage(p => Math.min(totalPages, p + 1))}
            className={cn('chevron', { disabled: page === totalPages, right: true })}
          />
        </div>
      )}
    </>
  )
}

export default FileList
